/** Catering Packages — plain action creators (`type` + `payload`). */
import { CPK } from './cateringPackagesTypes';
import type { CreatePackagePayload, PackageFailure, PackageSavePayload } from './cateringPackagesTypes';
import type { CateringPackage, PackageCatalogItem } from '../../components/catering/data/types';

// ── Fetch ───────────────────────────────────────────────────────────────────────

/** `silent` = refresh in the background without flipping `loading` (used after mutations). */
export const fetchPackagesRequest = (opts: { silent?: boolean } = {}) =>
  ({ type: CPK.FETCH_REQUEST, payload: { silent: opts.silent === true } } as const);

export const fetchPackagesSuccess = (data: { packages: CateringPackage[]; locationId: string }) =>
  ({ type: CPK.FETCH_SUCCESS, payload: data } as const);

export const fetchPackagesFailure = (error: string) =>
  ({ type: CPK.FETCH_FAILURE, payload: { error } } as const);

// ── Details (one package, dedicated endpoint) ───────────────────────────────────

export const fetchPackageDetailRequest = (id: string) =>
  ({ type: CPK.DETAIL_FETCH_REQUEST, payload: { id } } as const);
export const fetchPackageDetailSuccess = (pkg: CateringPackage) =>
  ({ type: CPK.DETAIL_FETCH_SUCCESS, payload: { package: pkg } } as const);
/** `notFound`: the BE answered 404 / `NOT_FOUND` (missing, deleted, or another location's package). */
export const fetchPackageDetailFailure = (failure: { id: string; message: string; notFound?: boolean }) =>
  ({ type: CPK.DETAIL_FETCH_FAILURE, payload: failure } as const);

// ── Packages ────────────────────────────────────────────────────────────────────

export const createPackageRequest = (data: CreatePackagePayload) =>
  ({ type: CPK.PACKAGE_CREATE_REQUEST, payload: data } as const);
/** `imageError`: the package saved but its image step failed (see `CateringPackagesState.imageError`). */
export const createPackageSuccess = (pkg: CateringPackage, imageError?: string) =>
  ({ type: CPK.PACKAGE_CREATE_SUCCESS, payload: { package: pkg, imageError } } as const);
export const createPackageFailure = (failure: PackageFailure) =>
  ({ type: CPK.PACKAGE_CREATE_FAILURE, payload: failure } as const);

export const updatePackageRequest = (id: string, data: PackageSavePayload) =>
  ({ type: CPK.PACKAGE_UPDATE_REQUEST, payload: { id, data } } as const);
export const updatePackageSuccess = (pkg: CateringPackage, imageError?: string) =>
  ({ type: CPK.PACKAGE_UPDATE_SUCCESS, payload: { package: pkg, imageError } } as const);
export const updatePackageFailure = (failure: PackageFailure) =>
  ({ type: CPK.PACKAGE_UPDATE_FAILURE, payload: failure } as const);

/** `name` omitted → BE default "<name> (Copy)". Copies tags, variants and items. */
export const duplicatePackageRequest = (id: string, name?: string) =>
  ({ type: CPK.PACKAGE_DUPLICATE_REQUEST, payload: { id, name } } as const);
export const duplicatePackageSuccess = (pkg: CateringPackage) =>
  ({ type: CPK.PACKAGE_DUPLICATE_SUCCESS, payload: { package: pkg } } as const);
export const duplicatePackageFailure = (failure: PackageFailure) =>
  ({ type: CPK.PACKAGE_DUPLICATE_FAILURE, payload: failure } as const);

export const deletePackageRequest = (id: string) =>
  ({ type: CPK.PACKAGE_DELETE_REQUEST, payload: { id } } as const);
export const deletePackageSuccess = (id: string) =>
  ({ type: CPK.PACKAGE_DELETE_SUCCESS, payload: { id } } as const);
export const deletePackageFailure = (failure: PackageFailure) =>
  ({ type: CPK.PACKAGE_DELETE_FAILURE, payload: failure } as const);

// ── Reorder (drag) ──────────────────────────────────────────────────────────────

/** One package whose position changed. */
export interface PackageOrderChange {
  id: string;
  sortOrder: number;
}

/**
 * `orderedIds` = the new order of the whole (unfiltered) package list; the reducer renumbers them `0..n-1`.
 * `changes` = only the packages whose position actually differs — the saga PATCHes exactly those
 * (`PATCH /catering/packages/:id { sortOrder }`, no dedicated endpoint; the BE stores it as sent).
 */
export const reorderPackagesRequest = (orderedIds: string[], changes: PackageOrderChange[]) =>
  ({ type: CPK.REORDER_REQUEST, payload: { orderedIds, changes } } as const);
export const reorderPackagesSuccess = () => ({ type: CPK.REORDER_SUCCESS } as const);
export const reorderPackagesFailure = (failure: PackageFailure) =>
  ({ type: CPK.REORDER_FAILURE, payload: failure } as const);

// ── Package item picker (catalogue items + tray sizes) ──────────────────────────

export const fetchPackageCatalogItemsRequest = () => ({ type: CPK.CATALOG_FETCH_REQUEST } as const);
export const fetchPackageCatalogItemsSuccess = (items: PackageCatalogItem[], locationId: string) =>
  ({ type: CPK.CATALOG_FETCH_SUCCESS, payload: { items, locationId } } as const);
export const fetchPackageCatalogItemsFailure = (error: string) =>
  ({ type: CPK.CATALOG_FETCH_FAILURE, payload: { error } } as const);

export const clearPackagesSaveState = () => ({ type: CPK.CLEAR_SAVE_STATE } as const);

export type CateringPackagesAction =
  | ReturnType<typeof fetchPackagesRequest>
  | ReturnType<typeof fetchPackagesSuccess>
  | ReturnType<typeof fetchPackagesFailure>
  | ReturnType<typeof fetchPackageDetailRequest>
  | ReturnType<typeof fetchPackageDetailSuccess>
  | ReturnType<typeof fetchPackageDetailFailure>
  | ReturnType<typeof createPackageRequest>
  | ReturnType<typeof createPackageSuccess>
  | ReturnType<typeof createPackageFailure>
  | ReturnType<typeof updatePackageRequest>
  | ReturnType<typeof updatePackageSuccess>
  | ReturnType<typeof updatePackageFailure>
  | ReturnType<typeof duplicatePackageRequest>
  | ReturnType<typeof duplicatePackageSuccess>
  | ReturnType<typeof duplicatePackageFailure>
  | ReturnType<typeof deletePackageRequest>
  | ReturnType<typeof deletePackageSuccess>
  | ReturnType<typeof deletePackageFailure>
  | ReturnType<typeof reorderPackagesRequest>
  | ReturnType<typeof reorderPackagesSuccess>
  | ReturnType<typeof reorderPackagesFailure>
  | ReturnType<typeof fetchPackageCatalogItemsRequest>
  | ReturnType<typeof fetchPackageCatalogItemsSuccess>
  | ReturnType<typeof fetchPackageCatalogItemsFailure>
  | ReturnType<typeof clearPackagesSaveState>;
